import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'

import Btn from '../ui/Btn'
import VideoModal from '../ui/VideoModal'
import { posterUrl } from '../ui/videoSource'
import './challenges.css'

/*
 * THE VIDEO LIBRARY PICKER.
 *
 * GET  {VIDEOSERVER}/upload/{APP_NAME}                 the library listing
 * POST admin/challenges/challengevideos { addVideo, ChallengeID, VideoID }
 * Both untouched: same URLs, same payload keys, same headers, and the same
 * reading of the answer (err==0 -> reload the caller's list and close it).
 *
 * The control that attaches a clip is still a real checkbox — the request is
 * built from `e.target.checked` exactly as before — but it now lives inside a
 * card-sized label, so it is a 40px target instead of a 13px one.
 *
 * This component is also rendered from other screens (CoachesVideosSelector)
 * inside their own dialogs, so it deliberately owns no page chrome and nothing
 * positioned: it is a toolbar and a grid, and it fits whatever box it is given.
 *
 * Two fixes worth naming:
 *   - the search filtered the ALREADY filtered array, so deleting a character
 *     never brought the wider results back. It now always filters the full
 *     listing.
 *   - a clip with no friendlyName was dropped from the search results
 *     entirely. It is matched on its id like everything else.
 */

const IconPlay = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.2v13.6L19 12 8 5.2z" /></svg>
)

const IconPlus = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
)

const IconFilm = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5.5h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1zm6 3v7l6-3.5-6-3.5z" />
  </svg>
)

const newest=(a,b)=>((a.Timestamp<b.Timestamp) ? 1 : -1)

const duration=(value)=>{
    const total=parseInt(value)
    if (!Number.isFinite(total) || total<0) return ''
    const min=parseInt(total/60)
    const sec=total-min*60
    return min+':'+(sec<10 ? '0'+sec : sec)
}

export default function ChallengeSelectVideo(props) {

    const [videos,setVideos]=useState([])
    const [videos1,setVideos1]=useState([])
    const [isLoading,setIsLoading]=useState(true)
    const [failed,setFailed]=useState(false)
    const [note,setNote]=useState(null)
    const [search,setSearch]=useState('')
    const [clipIndex,setClipIndex]=useState(-1)

    useEffect(()=>{
        loadVideos()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[])

    const loadVideos=()=>{
        setIsLoading(true)
        setFailed(false)
        axios.get(process.env.REACT_APP_VIDEOSERVER_API+'upload/'+process.env.REACT_APP_NAME)
        .then(res=>{
            setVideos(res.data.sort((a,b)=>(a.Timestamp<b.Timestamp) ? 1 : -1))
            setVideos1(res.data.sort((a,b)=>(a.Timestamp<b.Timestamp) ? 1 : -1))
            setIsLoading(false)
        }).catch(err=>{
            setIsLoading(false)
            setFailed(true)
            console.log(err)
        })
    }

    /* The full listing is the one source the search reads from, so backspacing
       widens the results again. */
    const library=useMemo(()=>{
        const all=(Array.isArray(videos1) && videos1.length>0 ? videos1 : videos)
        const list=Array.isArray(all) ? all.filter(item=>item && item.id) : []
        const s=search.trim()

        const found=s.length>0
            ? list.filter(item=>{
                const name=item.friendlyName ? String(item.friendlyName) : ''
                return String(item.id).indexOf(s)>-1 || name.indexOf(s)>-1
              })
            : list.slice()

        return found.sort(newest)
    },[videos,videos1,search])

    const open=clipIndex>=0 && clipIndex<library.length

    useEffect(()=>{
        if (clipIndex>=library.length) setClipIndex(-1)
    },[library.length,clipIndex])

    const closeClip=useCallback(()=>setClipIndex(-1),[])


const selectVideo=(e,VideoID)=>{

    setNote(null)

    const obj={
        addVideo:e.target.checked,
        ChallengeID:props.ChallengeID,
        VideoID
    }
    axios.post(process.env.REACT_APP_API_SERVER+'admin/challenges/challengevideos',obj,{
        headers: {
            'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
            }
        })
      .then(res=>{
        //props.showForm(0,false)
        if (res.data.err==0){
            props.loadData()
            props.handleClose()
        }else{
            setNote({tone:'crit',text:'Υπήρξε ένα πρόβλημα, παρακαλώ προσπαθήστε ξανά'})
        }
      }).catch(err=>{
        setNote({tone:'crit',text:'Υπήρξε ένα πρόβλημα, παρακαλώ προσπαθήστε ξανά'})
      })
}


    const cards=library.map((item,index)=>{
        const poster=posterUrl(item.id)
        const label=item.friendlyName ? item.friendlyName : item.id
        const dur=duration(item.duration)

        return(
            <li className="chx-vcard" key={item.id}>
                <button
                    type="button"
                    className="chx-thumb"
                    onClick={()=>setClipIndex(index)}
                    title="Προεπισκόπηση"
                    aria-label={'Προεπισκόπηση — '+label}
                >
                    {poster
                        ? <img src={poster} alt="" loading="lazy" decoding="async"/>
                        : <span className="chx-thumb-blank">Χωρίς εικόνα</span>}
                    <span className="chx-play" aria-hidden="true"><IconPlay/></span>
                </button>

                <div className="chx-vbody">
                    <span className="chx-vname">{item.friendlyName ? item.friendlyName : '—'}</span>
                    <span className="chx-vid">{item.id}</span>
                    {dur ? <span className="chx-vid">Διάρκεια {dur}</span> : null}
                </div>

                <div className="chx-vfoot">
                    <Btn onClick={()=>setClipIndex(index)}>
                        <IconPlay/>
                        Προβολή
                    </Btn>

                    <label className="chx-pick">
                        <input type="checkbox" onClick={(e)=>selectVideo(e,item.id)}/>
                        <IconPlus/>
                        Προσθήκη
                    </label>
                </div>
            </li>
        )
    })


    return (
        <div className="chx-form">

            <div className="chx-toolbar">
                <div className="chx-field">
                    <label className="chx-label" htmlFor="csv-search">Αναζήτηση</label>
                    <input
                        id="csv-search"
                        className="chx-input"
                        type="search"
                        value={search}
                        onChange={(e)=>setSearch(e.target.value)}
                        placeholder="Φιλική ονομασία ή κωδικός…"
                    />
                </div>

                <div className="chx-toolbar-actions">
                    <Btn onClick={()=>setSearch('')}>Καθαρισμός</Btn>
                    <Btn onClick={loadVideos}>Ανανέωση</Btn>
                </div>
            </div>

            {note && (
                <div className="chx-note chx-note-crit" role="alert">
                    <span className="chx-note-text">{note.text}</span>
                </div>
            )}

            {isLoading && (
                <div className="chx-sk-grid">
                    <div className="skeleton chx-sk-card"></div>
                    <div className="skeleton chx-sk-card"></div>
                    <div className="skeleton chx-sk-card"></div>
                </div>
            )}

            {!isLoading && failed && (
                <div className="chx-note chx-note-crit" role="alert">
                    <span className="chx-note-text">Η βιβλιοθήκη βίντεο δεν φορτώθηκε.</span>
                    <Btn onClick={loadVideos}>Δοκιμή ξανά</Btn>
                </div>
            )}

            {!isLoading && !failed && library.length===0 && (
                <div className="chx-empty">
                    <span className="chx-empty-mark" aria-hidden="true"><IconFilm/></span>
                    <h2 className="chx-empty-title">Κανένα βίντεο</h2>
                    <p className="chx-empty-text">
                        {search.trim().length>0
                            ? 'Κανένα βίντεο δεν ταιριάζει με την αναζήτηση. Καθαρίστε το πεδίο για να δείτε όλη τη βιβλιοθήκη.'
                            : 'Η βιβλιοθήκη είναι άδεια. Ανεβάστε πρώτα ένα βίντεο στον διακομιστή βίντεο.'}
                    </p>
                </div>
            )}

            {!isLoading && !failed && library.length>0 && (
                <ul className="chx-grid">{cards}</ul>
            )}

            {open && (
                <VideoModal
                    items={library.map(item=>({
                        fileId: item.id,
                        name: item.friendlyName ? item.friendlyName : item.id,
                        subtitle: item.id
                    }))}
                    index={clipIndex}
                    title="Βιβλιοθήκη βίντεο"
                    onIndex={setClipIndex}
                    onClose={closeClip}
                />
            )}

        </div>

    )
}
